"use client";

import { Pause, Play, SkipBack, SkipForward } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib";
import { Button } from "../button";
import { MediaPlayerAmbientBackdrop } from "./media-player-ambient-backdrop";
import {
	MediaPlayerCardSeekBar,
	TOPIC_LEARN_SCRUB_THUMB_DIAMETER_PX,
} from "./media-player-card-seek-bar";
import type { MediaPlayerSeekSegment } from "./media-player-seek-bar";
import { MediaPlayerSeekBar } from "./media-player-seek-bar";
import { MediaPlayerSkipLabel } from "./media-player-skip-label";
import { MediaPlayerSpeedChips } from "./media-player-speed-chips";
import { MediaPlayerStaticProgressTrack } from "./media-player-static-progress-track";
import type { MediaPlayerTheme } from "./media-player-theme";
import { MediaPlayerTrackParticles } from "./media-player-track-particles";

export type MediaPlayerVariant = "card" | "topicAudio";

export interface MediaPlayerControlsProps {
	variant: MediaPlayerVariant;
	theme: MediaPlayerTheme;
	progressPercent: number;
	isPlaying: boolean;
	onPlayPause: () => void;
	currentTimeLabel?: string;
	remainingTimeLabel?: string;
	skipIntervalSeconds?: number;
	onSkipBack?: () => void;
	onSkipForward?: () => void;
	maxSeekPercent?: number;
	/** Continuation timer fill behind the playhead (card-local 0–100). */
	continuationPercent?: number;
	furthestConsumedPercent?: number;
	onSeekCommit?: (percent: number) => void;
	onSeekRejected?: () => void;
	/** Card scrub: pause autoplay without toggling play button state semantics. */
	onSeekPause?: () => void;
	cardSegments?: MediaPlayerSeekSegment[];
	readingPaceSlot?: ReactNode;
	playbackRate?: number;
	onPlaybackRateChange?: (rate: number) => void;
	getSpeedChipLabel?: (rate: number) => string;
	ambientBackdrop?: boolean;
	/** Soft aurora glow behind the progress track (default on). */
	trackGlow?: boolean;
	disabled?: boolean;
	/** When true, primary control advances to the next card instead of play/pause. */
	atSegmentEnd?: boolean;
	/** When true, primary control returns to the topic cover instead of play/pause. */
	atTopicContentEnd?: boolean;
	returnToStartAriaLabel?: string;
	/** Cover slide while paused — skip-forward affordance to start the topic. */
	onCoverSlide?: boolean;
	coverAdvanceAriaLabel?: string;
	playAriaLabel?: string;
	pauseAriaLabel?: string;
	rewindAriaLabel?: string;
	skipBackAriaLabel?: string;
	skipForwardAriaLabel?: string;
	seekLockedLabel?: string;
	/** Card variant: wide = pill track; narrow = square, edge-to-edge track. */
	cardLayout?: "narrow" | "wide";
	className?: string;
}

export function MediaPlayerControls({
	variant,
	theme,
	progressPercent,
	isPlaying,
	onPlayPause,
	currentTimeLabel,
	remainingTimeLabel,
	skipIntervalSeconds = 10,
	onSkipBack,
	onSkipForward,
	maxSeekPercent = 100,
	continuationPercent,
	furthestConsumedPercent = maxSeekPercent,
	onSeekCommit,
	onSeekRejected,
	onSeekPause,
	cardSegments,
	readingPaceSlot,
	playbackRate = 1,
	onPlaybackRateChange,
	getSpeedChipLabel,
	ambientBackdrop = variant === "topicAudio",
	trackGlow = true,
	disabled = false,
	atSegmentEnd = false,
	atTopicContentEnd = false,
	returnToStartAriaLabel = "Return to Start",
	onCoverSlide = false,
	coverAdvanceAriaLabel = "Advance and Play",
	playAriaLabel = "Play",
	pauseAriaLabel = "Pause",
	skipBackAriaLabel,
	skipForwardAriaLabel,
	seekLockedLabel,
	cardLayout = "wide",
	className,
}: MediaPlayerControlsProps) {
	const isCard = variant === "card";
	const showBarParticles = trackGlow && !(ambientBackdrop && !isCard);
	const skipLabel = `${skipIntervalSeconds} seconds`;
	const resolvedSkipBackLabel = skipBackAriaLabel ?? `Skip back ${skipLabel}`;
	const resolvedSkipForwardLabel =
		skipForwardAriaLabel ?? `Skip forward ${skipLabel}`;

	const showSkipForward = atSegmentEnd || onCoverSlide;

	const playPauseButton = (
		<Button
			type="button"
			size="icon"
			variant="secondary"
			className={theme.playButtonClassName}
			disabled={disabled}
			onClick={onPlayPause}
			aria-label={
				atTopicContentEnd
					? returnToStartAriaLabel
					: showSkipForward
						? onCoverSlide
							? coverAdvanceAriaLabel
							: "Next card"
						: isPlaying
							? pauseAriaLabel
							: playAriaLabel
			}
		>
			{atTopicContentEnd ? (
				<SkipBack className="size-4" />
			) : showSkipForward ? (
				<SkipForward className="size-4" />
			) : isPlaying ? (
				<Pause className="size-4" />
			) : (
				<Play className="size-4" />
			)}
		</Button>
	);

	const cardUsesSeekBar = isCard && Boolean(onSeekCommit);

	const progressTrack = isCard ? (
		cardUsesSeekBar ? (
			<MediaPlayerCardSeekBar
				theme={theme}
				value={progressPercent}
				maxSeekPercent={maxSeekPercent}
				continuationPercent={continuationPercent}
				furthestConsumedPercent={furthestConsumedPercent}
				roundedFull={cardLayout !== "narrow"}
				showThumb={false}
				showSeekPin
				thumbDiameterPx={TOPIC_LEARN_SCRUB_THUMB_DIAMETER_PX}
				segments={cardSegments}
				interactive={!disabled}
				trackGlow={trackGlow && cardLayout !== "narrow"}
				onPause={() => {
					if (onSeekPause) {
						onSeekPause();
						return;
					}
					if (isPlaying) {
						onPlayPause();
					}
				}}
				onSeekCommit={(percent) => onSeekCommit?.(percent)}
				onSeekRejected={onSeekRejected}
			/>
		) : (
			<MediaPlayerStaticProgressTrack
				theme={theme}
				progressPercent={progressPercent}
				trackGlow={trackGlow}
				cardLayout={cardLayout}
			/>
		)
	) : (
		<MediaPlayerSeekBar
			theme={theme}
			value={progressPercent}
			maxSeekPercent={maxSeekPercent}
			furthestConsumedPercent={furthestConsumedPercent}
			segments={cardSegments}
			interactive={!disabled}
			trackGlow={trackGlow}
			onPause={() => {
				if (isPlaying) {
					onPlayPause();
				}
			}}
			onSeekCommit={(percent) => onSeekCommit?.(percent)}
			onSeekRejected={onSeekRejected}
			seekLockedLabel={seekLockedLabel}
		/>
	);

	const inner = (
		<div
			className={cn(
				"relative flex w-full items-center gap-2",
				showBarParticles && "min-h-11 overflow-visible",
				trackGlow && !showBarParticles && "overflow-visible",
				disabled && "pointer-events-none opacity-0",
				className,
			)}
			aria-hidden={disabled}
		>
			{showBarParticles ? (
				<MediaPlayerTrackParticles theme={theme} />
			) : null}
			{isCard ? (
				<div
					className={cn(
						"w-full min-w-0",
						showBarParticles && "relative z-[1] overflow-visible",
					)}
				>
					{progressTrack}
				</div>
			) : (
				<div
					className={cn(
						"flex w-full min-w-0 flex-col gap-2",
						showBarParticles && "relative z-[1] overflow-visible",
					)}
				>
					<div className="flex min-w-0 items-center gap-2">
						{currentTimeLabel ? (
							<span
								className={cn(
									"shrink-0 tabular-nums font-black",
									theme.timestampClassName,
								)}
							>
								{currentTimeLabel}
							</span>
						) : null}
						{readingPaceSlot ? (
							<div className="shrink-0">{readingPaceSlot}</div>
						) : null}
						<Button
							type="button"
							size="icon"
							variant="ghost"
							className={theme.skipButtonClassName}
							disabled={disabled}
							onClick={onSkipBack}
							aria-label={resolvedSkipBackLabel}
						>
							<MediaPlayerSkipLabel
								direction="back"
								skipIntervalSeconds={skipIntervalSeconds}
								iconClassName="size-4"
								toneClassName="text-current"
							/>
						</Button>
						<div className="shrink-0">{playPauseButton}</div>
						<Button
							type="button"
							size="icon"
							variant="ghost"
							className={theme.skipButtonClassName}
							disabled={disabled}
							onClick={onSkipForward}
							aria-label={resolvedSkipForwardLabel}
						>
							<MediaPlayerSkipLabel
								direction="forward"
								skipIntervalSeconds={skipIntervalSeconds}
								iconClassName="size-4"
								toneClassName="text-current"
							/>
						</Button>
						<div
							className={cn(
								"min-w-0 flex-1",
								trackGlow && "overflow-visible",
							)}
						>
							{progressTrack}
						</div>
						{remainingTimeLabel ? (
							<span
								className={cn(
									"shrink-0 tabular-nums font-black",
									theme.timestampClassName,
								)}
							>
								{remainingTimeLabel}
							</span>
						) : null}
						{onPlaybackRateChange ? (
							<MediaPlayerSpeedChips
								theme={theme}
								value={playbackRate}
								onChange={onPlaybackRateChange}
								disabled={disabled}
								getChipLabel={getSpeedChipLabel}
							/>
						) : null}
					</div>
				</div>
			)}
		</div>
	);

	if (ambientBackdrop && !isCard) {
		return (
			<MediaPlayerAmbientBackdrop
				theme={theme}
				className="rounded-2xl p-3"
			>
				{inner}
			</MediaPlayerAmbientBackdrop>
		);
	}

	return inner;
}
